import { useMutation, useQuery } from '@tanstack/react-query';
import palette from '^/constants/palette';
import { useL10n } from '^/hooks/useL10n';
import { displayToast } from '^/hooks/usePresentationUtils';
import { makeAuthHeader, makeV2APIURL, makeVersionHeader } from '^/store/duck/API';
import { queryClient } from '^/store/react-query';
import * as T from '^/types';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast, ToastOptions } from 'react-toastify';
import {
  deleteViewpointAlbum,
  deleteViewpointPhoto,
  getV2BAlbumById,
  getV2BAlbums,
  renameViewpointAlbum,
  requestDroneCapture,
} from './services';
import Text from './text';
import { sortPhotoAlbums } from './utils';
import { defaultToastErrorOption, useToast } from '^/hooks/useToastify';
import { ErrorText } from '^/hooks/useToastify/text';

export const V2BQueryKeys = {
  requestViewpoint: ['saved', 'viewports'],
  v2bAlbums: ['v2bAlbums'],
  v2bAlbumById: (albumId: T.PhotoAlbum['id']) => ['v2bAlbums', albumId],
};

// const commonQueryOptions: UseQueryOptions<T.PhotoAlbum[], unknown, T.PhotoAlbum[], string[]> = {
//   refetchOnMount: false,
//   refetchOnWindowFocus: false,
// };

const toastOptions: ToastOptions = {
  position: 'bottom-right',
  hideProgressBar: true,
  style: {
    color: palette.darkBlack.toString(),
    fontWeight: 500,
  },
};

export function useRequestDroneCapture() {
  const [l10n] = useL10n();
  const toastify = useToast();
  const { id: projectId } = useParams();
  return useMutation({
    mutationFn: async ({ photoAlbumId }: { photoAlbumId: T.RequestPhoto['id'] }) => {
      const res = await requestDroneCapture(photoAlbumId);
      return res;
    },
    onSuccess: async (...args) => {
      const variables = args[1];
      await queryClient.invalidateQueries(V2BQueryKeys.v2bAlbumById(variables.photoAlbumId));
      if (projectId) {
        await queryClient.invalidateQueries(V2BQueryKeys.v2bAlbums);
      }
      toast(displayToast(l10n(Text.requestCompleted)), toastOptions);
    },
    onError: error => {
      console.error(error);
      toastify({
        type: T.Toast.ERROR,
        content: {
          title: ErrorText.networkError.title,
          description: ErrorText.networkError.description,
        },
        option: defaultToastErrorOption,
      });
    },
    networkMode: 'always',
  });
}

export function useV2BAlbumsQuery() {
  const Auth = useSelector((s: T.State) => s.Auth);
  const slug = useSelector((s: T.State) => s.PlanConfig.config?.slug);
  const authHeader = makeAuthHeader(Auth, slug);
  const versionHeader = makeVersionHeader();
  const { id: projectId } = useParams();
  const result = useQuery({
    queryKey: V2BQueryKeys.v2bAlbums,
    queryFn: async () => getV2BAlbums(Number(projectId), authHeader!, versionHeader),
    enabled: Boolean(projectId) && Boolean(authHeader),
  });

  const sortedResult = sortPhotoAlbums(result.data ?? []);

  return { ...result, data: sortedResult };
}

export function useV2BAlbumByIdQuery(albumId: T.PhotoAlbum['id']) {
  const Auth = useSelector((s: T.State) => s.Auth);
  const slug = useSelector((s: T.State) => s.PlanConfig.config?.slug);
  const authHeader = makeAuthHeader(Auth, slug);
  const versionHeader = makeVersionHeader();
  const photoAlbumByIdURL: string = makeV2APIURL('photo_albums', albumId);

  return useQuery({
    queryKey: V2BQueryKeys.v2bAlbumById(albumId),
    queryFn: async () => getV2BAlbumById(photoAlbumByIdURL, authHeader!, versionHeader),
    enabled: Boolean(albumId) && Boolean(authHeader),
  });
}

interface RenameV2BAlbumContext {
  previousAlbums: any;
  previousV2BAlbum: any;
  photoAlbumId: T.PhotoAlbum['id'];
}

export const useRenameV2BAlbumMutation = () => {
  const auth = useSelector((state: T.State) => state.Auth);
  const planConfigSlug = useSelector((state: T.State) => state.PlanConfig.config?.slug);
  return useMutation({
    mutationFn: async ({
      photoAlbumId,
      newTitle,
    }: {
      photoAlbumId: T.PhotoAlbum['id'];
      newTitle: string;
    }) =>
      renameViewpointAlbum({
        albumId: photoAlbumId,
        newTitle,
        auth,
        planConfigSlug,
      }),
    onMutate: async ({ photoAlbumId, newTitle }) => {
      // Cancel any outgoing refetches for both regular photo albums and v2b albums

      await queryClient.cancelQueries({ queryKey: V2BQueryKeys.v2bAlbumById(photoAlbumId) });

      const previousV2BAlbum = queryClient.getQueryData(V2BQueryKeys.v2bAlbumById(photoAlbumId));

      // Update the v2b album data
      queryClient.setQueryData(V2BQueryKeys.v2bAlbumById(photoAlbumId), (old: T.PhotoAlbumbyId) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          title: newTitle,
        };
      });

      return { previousV2BAlbum, photoAlbumId } as RenameV2BAlbumContext;
    },
    onError: (error, _, context: RenameV2BAlbumContext | undefined) => {
      // Rollback on error
      if (context && context.previousV2BAlbum) {
        queryClient.setQueryData(
          V2BQueryKeys.v2bAlbumById(context.photoAlbumId),
          context.previousV2BAlbum
        );
      }
      console.error('Failed to rename viewpoint album:', error);
    },
    onSuccess: async (_, { photoAlbumId }) => {
      // Invalidate queries to fetch the latest data
      void queryClient.invalidateQueries({
        queryKey: V2BQueryKeys.v2bAlbums,
      });
      void queryClient.invalidateQueries({
        queryKey: V2BQueryKeys.v2bAlbumById(photoAlbumId),
      });
    },
  });
};

export function useDeleteV2BAlbumMutation() {
  const auth = useSelector((state: T.State) => state.Auth);
  const slug = useSelector((state: T.State) => state.PlanConfig.config?.slug);
  const authHeader = makeAuthHeader(auth, slug);
  const versionHeader = makeVersionHeader();
  return useMutation({
    mutationFn: async ({ photoAlbumId }: { photoAlbumId: T.PhotoAlbum['id'] }) =>
      deleteViewpointAlbum(photoAlbumId, authHeader!, versionHeader),
  });
}

export function useDeleteViewpointPhotoMutation() {
  const auth = useSelector((state: T.State) => state.Auth);
  const slug = useSelector((state: T.State) => state.PlanConfig.config?.slug);
  const authHeader = makeAuthHeader(auth, slug);
  const { contentId: selectedAlbumId } = useParams();

  return useMutation({
    mutationFn: async ({ photoId }: { photoId: T.Photo['id'] }) =>
      deleteViewpointPhoto({ photoId, authHeader: authHeader! }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: V2BQueryKeys.v2bAlbumById(Number(selectedAlbumId)),
      });
    },
  });
}
